import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext.tsx';
import { useNotifications } from '../../context/NotificationContext.tsx';
import { useRealtime } from '../../context/RealtimeContext.tsx';
import { Sidebar } from './Sidebar.tsx';
import { Header } from './Header.tsx';
import { CompanyDashboard } from '../dashboard/CompanyDashboard.tsx';
import { ProjectListView } from '../projects/ProjectListView.tsx';
import { ProjectDetailView } from '../projects/ProjectDetailView.tsx';
import { ChatWorkspace } from '../chat/ChatWorkspace.tsx';
import { FloatingChatPopup } from '../chat/FloatingChatPopup.tsx';
import { MeetingsWorkspace } from '../meetings/MeetingsWorkspace.tsx';
import { ViewErrorBoundary } from '../common/ViewErrorBoundary.tsx';
import { TeamWorkloadView } from '../team/TeamWorkloadView.tsx';
import { TeamsWorkspace } from '../teams/TeamsWorkspace.tsx';
import { AuditLogView } from '../admin/AuditLogView.tsx';
import { GlobalCommandPalette } from '../search/GlobalCommandPalette.tsx';
import { CreateTaskModal } from '../tasks/CreateTaskModal.tsx';
import { TaskDetailModal } from '../tasks/TaskDetailModal.tsx';
import { TaskTable } from '../projects/TaskTable.tsx';
import { KanbanBoard } from '../projects/KanbanBoard.tsx';
import { TimelineView } from '../projects/TimelineView.tsx';
import { CalendarView } from '../projects/CalendarView.tsx';
import { StageFilterBar, useStageFilter, filterByStage } from '../common/StageFilterBar.tsx';
import { Task, TaskStatus } from '../../../shared/types.ts';
import {
  Check,
  X,
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  MessageSquare,
  Menu,
  Briefcase,
  Search,
  ChevronDown,
  Users,
  LayoutGrid,
  List,
  Clock,
  Calendar as CalendarIcon,
} from 'lucide-react';
import { VideoCallModal } from '../calls/VideoCallModal.tsx';
import { IncomingCallModal } from '../calls/IncomingCallModal.tsx';
import { OnboardingTourModal } from '../onboarding/OnboardingTourModal.tsx';
import { UserProfileModal } from '../profile/UserProfileModal.tsx';
import { pathToState, stateToPath } from '../../routes.ts';
import { useUserProfileModal } from '../../context/UserProfileModalContext.tsx';
import { useChatUnread } from '../../context/ChatUnreadContext.tsx';
import { usePermissions } from '../../context/PermissionsContext.tsx';
import { RolesPermissionsView } from '../admin/RolesPermissionsView.tsx';
import { ProjectMultiSelect } from '../common/ProjectMultiSelect.tsx';
import { TeamSelect, useTeamMemberIds, taskInTeams } from '../common/TeamSelect.tsx';
import { NotificationPermissionPrompt } from '../notifications/NotificationPermissionPrompt.tsx';

export const AppLayout: React.FC = () => {
  const { user, token } = useAuth();
  const { toast, clearToast, unreadCount } = useNotifications();
  const { totalUnread: chatUnread } = useChatUnread();
  // Re-renders the whole app when an administrator changes role permissions
  const { can } = usePermissions();
  const { subscribe } = useRealtime();
  const { setChatNavigationCallback } = useUserProfileModal();

  // The browser tab title shows the number of unseen chat messages: "(3) Pulse - ..."
  const pageTitleBase = useRef(document.title.replace(/^\(\d+\+?\)\s*/, ''));
  const attentionCount = chatUnread;
  useEffect(() => {
    document.title = attentionCount > 0 ? `(${attentionCount > 99 ? '99+' : attentionCount}) ${pageTitleBase.current}` : pageTitleBase.current;
    return () => {
      document.title = pageTitleBase.current;
    };
  }, [attentionCount]);

  // The URL decides the starting screen, so a reload (or a shared link) stays where it was
  const [initialRoute] = useState(() => pathToState(window.location.pathname, window.location.search));
  const [currentView, setCurrentView] = useState<string>(initialRoute.view);
  const [activeBoardProjectId, setActiveBoardProjectId] = useState<string>(() => {
    return localStorage.getItem('ihb-active-project-id') || '';
  });
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCommandPaletteOpen, setIsCommandPaletteOpen] = useState(false);

  useEffect(() => {
    if (currentView.startsWith('project-')) {
      const pid = currentView.replace('project-', '');
      setActiveBoardProjectId(pid);
      localStorage.setItem('ihb-active-project-id', pid);
    }
  }, [currentView]);

  useEffect(() => {
    if (currentView === 'boards' && token) {
      fetch('/api/projects', {
        headers: {
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.projects && data.projects.length > 0) {
            const validIds = new Set(data.projects.map((p: any) => p.id));
            if (!activeBoardProjectId || !validIds.has(activeBoardProjectId)) {
              const firstId = data.projects[0].id;
              setActiveBoardProjectId(firstId);
              localStorage.setItem('ihb-active-project-id', firstId);
            }
          }
        })
        .catch(() => {});
    }
  }, [currentView, activeBoardProjectId, token, user?.id]);

  // Synchronize active board when Space is switched
  useEffect(() => {
    const handleSpaceChanged = (e: any) => {
      if (!token) return;
      const targetSpaceId = e.detail?.spaceId;
      const headers: Record<string, string> = {
        Authorization: `Bearer ${token}`,
        ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
      };
      if (targetSpaceId) {
        headers['x-space-id'] = targetSpaceId;
      }
      fetch('/api/projects', { headers })
        .then((r) => r.json())
        .then((data) => {
          if (data.projects && data.projects.length > 0) {
            const firstId = data.projects[0].id;
            setActiveBoardProjectId(firstId);
            localStorage.setItem('ihb-active-project-id', firstId);
          }
        })
        .catch(() => {});
    };

    window.addEventListener('space-changed', handleSpaceChanged);
    return () => window.removeEventListener('space-changed', handleSpaceChanged);
  }, [token, user?.id]);
  const [isCreateTaskOpen, setIsCreateTaskOpen] = useState(false);
  const [activeModalTaskId, setActiveModalTaskId] = useState<string | null>(initialRoute.taskId);
  const [chatTargetChannelId, setChatTargetChannelId] = useState<string | null>(initialRoute.channelId);

  // Keep the address bar in step with the screen (and open task), and follow back/forward
  useEffect(() => {
    const target = stateToPath({ view: currentView, taskId: activeModalTaskId, channelId: null });
    if (target !== window.location.pathname + window.location.search) {
      window.history.pushState({}, '', target);
    }
  }, [currentView, activeModalTaskId]);

  useEffect(() => {
    const handlePopState = () => {
      const route = pathToState(window.location.pathname, window.location.search);
      setCurrentView(route.view);
      setActiveModalTaskId(route.taskId);
      if (route.channelId) setChatTargetChannelId(route.channelId);
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
  const [lastNonChatView, setLastNonChatView] = useState<string>('boards');

  useEffect(() => {
    if (currentView !== 'chat') {
      setLastNonChatView(currentView);
    }
  }, [currentView]);

  // My Tasks view state
  const [myTasks, setMyTasks] = useState<Task[]>([]);
  // Every project the user can open, so the project filter lists them all (not just the ones with tasks)
  const [filterProjects, setFilterProjects] = useState<Array<{ id: string; name: string; key?: string }>>([]);

  // Keyboard shortcut Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        setIsCommandPaletteOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Hook universal user profile modal direct-chat callback
  useEffect(() => {
    setChatNavigationCallback((channelId: string) => {
      setChatTargetChannelId(channelId);
      setCurrentView('chat');
    });
  }, [setChatNavigationCallback]);

  const handleNotificationNavigate = (meta: {
    type?: string;
    channelId?: string;
    taskId?: string;
    projectId?: string;
    linkUrl?: string;
    title?: string;
  }) => {
    clearToast();

    let urlChannelId = meta.channelId;
    let urlTaskId = meta.taskId;
    let urlProjectId = meta.projectId;

    if (meta.linkUrl) {
      if (meta.linkUrl.startsWith('/chat')) {
        const match = meta.linkUrl.match(/[?&](?:channelId|channel)=([^&]+)/);
        if (match) urlChannelId = decodeURIComponent(match[1]);
        if (urlChannelId) setChatTargetChannelId(urlChannelId);
        setCurrentView('chat');
        return;
      }
      if (meta.linkUrl.startsWith('/projects/')) {
        const [pathPart, queryPart] = meta.linkUrl.split('?');
        urlProjectId = pathPart.replace('/projects/', '');
        if (queryPart) {
          const tMatch = queryPart.match(/(?:^|&)taskId=([^&]+)/);
          if (tMatch) urlTaskId = decodeURIComponent(tMatch[1]);
        }
        if (urlProjectId) setCurrentView(`project-${urlProjectId}`);
        if (urlTaskId) setActiveModalTaskId(urlTaskId);
        return;
      }
      if (meta.linkUrl.startsWith('/meetings')) {
        setCurrentView('meetings');
        return;
      }
    }

    if (
      urlChannelId ||
      meta.type === 'chat' ||
      meta.type === 'chat_message' ||
      meta.type === 'mention' ||
      (meta.title && meta.title.startsWith('💬'))
    ) {
      if (urlChannelId) {
        setChatTargetChannelId(urlChannelId);
      }
      setCurrentView('chat');
      return;
    }

    if (urlTaskId) {
      setActiveModalTaskId(urlTaskId);
      return;
    }

    if (urlProjectId) {
      setCurrentView(`project-${urlProjectId}`);
      return;
    }

    setCurrentView('chat');
  };

  useEffect(() => {
    const handleOpenTask = (e: any) => {
      if (e.detail?.taskId) {
        setActiveModalTaskId(e.detail.taskId);
      }
    };
    const handleNavigateNotif = (e: any) => {
      if (e.detail) {
        handleNotificationNavigate(e.detail);
      }
    };
    window.addEventListener('open-task-modal', handleOpenTask);
    window.addEventListener('navigate-notification', handleNavigateNotif);
    return () => {
      window.removeEventListener('open-task-modal', handleOpenTask);
      window.removeEventListener('navigate-notification', handleNavigateNotif);
    };
  }, []);

  const fetchMyTasks = () => {
    if (token) {
      fetch('/api/tasks?assigneeId=me', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) => setMyTasks(d.tasks || []))
        .catch(() => {});
      fetch('/api/projects', { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.json())
        .then((d) =>
          setFilterProjects((d.projects || []).map((p: any) => ({ id: p.id, name: p.name, key: p.key || p.code })))
        )
        .catch(() => {});
    }
  };

  // Fetch my tasks when in 'my-tasks' view
  useEffect(() => {
    if (currentView === 'my-tasks' && token) {
      fetchMyTasks();
    }
  }, [currentView, token]);

  // Real-time synchronization for My Tasks
  useEffect(() => {
    const handleTaskMutated = () => {
      if (currentView === 'my-tasks') {
        fetchMyTasks();
      }
    };
    window.addEventListener('task-created', handleTaskMutated);
    window.addEventListener('task-updated', handleTaskMutated);
    window.addEventListener('task-archived', handleTaskMutated);
    window.addEventListener('task-restored', handleTaskMutated);

    const unsubscribe = subscribe((event) => {
      if (
        event.type === 'task_created' ||
        event.type === 'task_updated' ||
        event.type === 'task_archived' ||
        event.type === 'task_restored'
      ) {
        if (currentView === 'my-tasks') {
          fetchMyTasks();
        }
      }
    });

    return () => {
      window.removeEventListener('task-created', handleTaskMutated);
      window.removeEventListener('task-updated', handleTaskMutated);
      window.removeEventListener('task-archived', handleTaskMutated);
      window.removeEventListener('task-restored', handleTaskMutated);
      unsubscribe();
    };
  }, [currentView, token, subscribe]);

  // Layout of the My Tasks screen, remembered per browser
  type MyTasksView = 'kanban' | 'list' | 'timeline' | 'calendar';
  const [myTasksView, setMyTasksView] = useState<MyTasksView>(() => {
    try {
      const saved = localStorage.getItem('myTasksView');
      if (saved === 'kanban' || saved === 'list' || saved === 'timeline' || saved === 'calendar') return saved;
    } catch {}
    return 'list';
  });
  const [myTasksSearch, setMyTasksSearch] = useState('');
  // My Tasks is only the tasks assigned to me, so there is no user filter; the projects they belong
  // to can be picked (one, several or all)
  const [myTasksProjectIds, setMyTasksProjectIds] = useState<string[]>([]);
  const [myTasksTeamIds, setMyTasksTeamIds] = useState<string[]>([]);
  const myTasksTeamMembers = useTeamMemberIds(myTasksTeamIds);
  const myTasksProjects = (() => {
    const map = new Map<string, { id: string; name: string; key?: string }>();
    filterProjects.forEach((p) => map.set(p.id, p));
    myTasks.forEach((t) => {
      if (t.projectId && !map.has(t.projectId)) {
        map.set(t.projectId, {
          id: t.projectId,
          name: t.projectName || 'Project',
          key: t.projectKey,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();
  const filteredMyTasks = myTasks.filter((t) => {
    if (myTasksProjectIds.length > 0 && !myTasksProjectIds.includes(t.projectId)) return false;
    if (!taskInTeams(t, myTasksTeamMembers)) return false;
    const q = myTasksSearch.trim().toLowerCase();
    return !q || `${t.title} ${t.code || ''}`.toLowerCase().includes(q);
  });
  const myTasksStage = useStageFilter();
  const visibleMyTasks = filterByStage(
    filteredMyTasks,
    myTasksStage.stage,
    myTasksStage.hideOverdue,
    myTasksStage.hideCompleted
  );
  const changeMyTasksView = (view: MyTasksView) => {
    setMyTasksView(view);
    try {
      localStorage.setItem('myTasksView', view);
    } catch {}
  };

  const handleUpdateMyTaskStatus = async (taskId: string, newStatus: TaskStatus) => {
    setMyTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t))
    );
    try {
      const res = await fetch(`/api/tasks/${taskId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-updated', { detail: data.task }));
        }
      }
    } catch (e) {}
  };

  const quickCreateMyTask = async (status: TaskStatus, title: string) => {
    if (!token || !title.trim()) return;
    const defaultProjId = myTasks[0]?.projectId || activeBoardProjectId || '';
    if (!defaultProjId) {
      setIsCreateTaskOpen(true);
      return;
    }
    try {
      const res = await fetch('/api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
          ...(user?.id ? { 'x-demo-user-id': user.id } : {}),
        },
        body: JSON.stringify({
          projectId: defaultProjId,
          title: title.trim(),
          status,
          priority: 'medium',
          assigneeId: user?.id || null,
          assignorId: user?.id || null,
        }),
      });
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        if (data.task) {
          window.dispatchEvent(new CustomEvent('task-created', { detail: data.task }));
        }
        fetchMyTasks();
      }
    } catch (err) {
      console.error('Failed to quick create task in My Tasks:', err);
    }
  };

  const getTitle = () => {
    if (currentView === 'dashboard') return 'Company Dashboard';
    if (currentView === 'projects') return 'Projects Portfolio';
    if (currentView === 'boards' || currentView.startsWith('project-')) return 'Board Workspace';
    if (currentView === 'my-tasks') return 'My Assigned Tasks';
    if (currentView === 'workload' || currentView === 'team') return 'Team Workload & Directory';
    if (currentView === 'chat') return 'Team Discussions & DMs';
    if (currentView === 'meetings') return 'Meetings & AI Transcriptions';
    if (currentView === 'teams' || currentView.startsWith('team-')) return 'Teams';
    if (currentView === 'audit') return 'Security & Audit Logs';
    if (currentView === 'roles') return 'Roles & Access';
    return 'Pulse';
  };

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-[#eaecf0] dark:bg-[#0c0e12] font-sans">
      {/* Sidebar (Desktop Persistent + Mobile Drawer) */}
      <Sidebar
        currentView={currentView}
        onNavigate={(view) => {
          setCurrentView(view);
          setIsMobileMenuOpen(false);
        }}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        isMobileOpen={isMobileMenuOpen}
        onCloseMobile={() => setIsMobileMenuOpen(false)}
      />

      {/* Main App Layout */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Header */}
        <Header
          currentViewTitle={getTitle()}
          onOpenSearch={() => setIsCommandPaletteOpen(true)}
          onOpenCreateTask={() => setIsCreateTaskOpen(true)}
          onOpenCreateProject={() => setCurrentView('projects')}
          onNavigate={(view) => setCurrentView(view)}
          onOpenMobileMenu={() => setIsMobileMenuOpen(true)}
        />

        {/* Viewport Content */}
        <main
          className={`flex-1 min-h-0 flex flex-col px-3 sm:px-5 lg:px-6 ${
            currentView === 'dashboard' ||
            currentView === 'boards' ||
            currentView === 'my-tasks' ||
            currentView === 'chat' ||
            currentView.startsWith('project-') ||
            currentView.startsWith('team-')
              ? 'overflow-hidden pb-2'
              : 'overflow-y-auto pb-24 md:pb-8 touch-scroll'
          }`}
        >
          <div
            className={
              currentView === 'dashboard' ||
              currentView === 'boards' ||
              currentView === 'my-tasks' ||
              currentView === 'chat' ||
              currentView.startsWith('project-') ||
              currentView.startsWith('team-')
                ? 'w-full flex-1 min-h-0 flex flex-col'
                : 'max-w-7xl w-full mx-auto'
            }
          >
            {/* View routing */}
            {currentView === 'dashboard' && (
              <ViewErrorBoundary viewName="Dashboard">
                <CompanyDashboard
                  key={user?.id || 'dashboard'}
                  onNavigateToProject={(projId) => setCurrentView(`project-${projId}`)}
                  onOpenTask={(taskId) => setActiveModalTaskId(taskId)}
                  onNavigate={(v) => setCurrentView(v)}
                />
              </ViewErrorBoundary>
            )}

            {currentView === 'projects' && (
              <ViewErrorBoundary viewName="Projects">
                <ProjectListView
                  onSelectProject={(projId) => {
                    setActiveBoardProjectId(projId);
                    setCurrentView(`project-${projId}`);
                  }}
                />
              </ViewErrorBoundary>
            )}

            {(currentView === 'boards' || currentView.startsWith('project-')) && (
              <ViewErrorBoundary viewName="Board Workspace">
                <ProjectDetailView
                  projectId={
                    currentView.startsWith('project-')
                      ? currentView.replace('project-', '')
                      : activeBoardProjectId
                  }
                  onBack={() => setCurrentView('projects')}
                  onSelectProject={(id) => {
                    setActiveBoardProjectId(id);
                    setCurrentView(`project-${id}`);
                  }}
                />
              </ViewErrorBoundary>
            )}

            {currentView === 'my-tasks' && (
              <div
                className={
                  myTasksView === 'kanban'
                    ? 'flex-1 min-h-0 flex flex-col gap-2 overflow-hidden'
                    : 'space-y-3 pb-24 md:pb-8 overflow-y-auto flex-1 min-h-0 pr-1 touch-scroll'
                }
              >
                <div className="flex flex-wrap sm:flex-nowrap items-center justify-between gap-2 px-0.5 shrink-0">
                  <StageFilterBar
                    tasks={filteredMyTasks}
                    stage={myTasksStage.stage}
                    onSelectStage={myTasksStage.selectStage}
                    hideOverdue={myTasksStage.hideOverdue}
                    hideCompleted={myTasksStage.hideCompleted}
                    onToggleHideOverdue={myTasksStage.toggleHideOverdue}
                    onToggleHideCompleted={myTasksStage.toggleHideCompleted}
                  />
                </div>
                {/* Same strip as the board workspace: title, search, View dropdown, then the user filter */}
                <div className="bg-white dark:bg-[#181b20] rounded-2xl px-3 py-2 border border-[#d5d8de]/60 dark:border-surface-800 shadow-2xs flex flex-nowrap items-center justify-between gap-2 shrink-0">
                  <div className="flex items-center space-x-2 min-w-0">
                    <div className="w-7 h-7 rounded-xl bg-[#121417] text-brand-400 flex items-center justify-center shrink-0">
                      {myTasksView === 'kanban' && <LayoutGrid className="w-3.5 h-3.5" />}
                      {myTasksView === 'list' && <List className="w-3.5 h-3.5" />}
                      {myTasksView === 'timeline' && <Clock className="w-3.5 h-3.5" />}
                      {myTasksView === 'calendar' && <CalendarIcon className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex items-center gap-2 min-w-0">
                      <h3 className="font-extrabold text-sm text-[#121417] dark:text-white shrink-0">
                        {myTasksView === 'kanban' && 'Sprint Kanban Board'}
                        {myTasksView === 'list' && 'Grid & Dense Spreadsheet'}
                        {myTasksView === 'timeline' && 'Interactive Gantt & Critical Path'}
                        {myTasksView === 'calendar' && 'Sprint Milestones Calendar'}
                      </h3>
                      <span className="text-[11px] font-bold px-2 py-0.2 rounded-full bg-brand-100 text-brand-700 dark:bg-brand-900/50 dark:text-brand-300 shrink-0">
                        {visibleMyTasks.length} cards
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-nowrap items-center gap-1.5 shrink-0">
                    <div className="relative w-36 sm:w-44">
                      <Search className="w-3 h-3 text-[#667085] dark:text-surface-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      <input
                        type="text"
                        value={myTasksSearch}
                        onChange={(e) => setMyTasksSearch(e.target.value)}
                        placeholder="Filter cards..."
                        className="w-full pl-7 pr-6 py-1 rounded-xl bg-[#f4f5f7] dark:bg-surface-800 border border-[#d5d8de] dark:border-surface-700 text-[11px] font-bold text-[#121417] dark:text-white placeholder:text-[#667085] dark:placeholder:text-surface-400 focus:outline-none focus:ring-1 focus:ring-brand-500"
                      />
                      {myTasksSearch && (
                        <button
                          onClick={() => setMyTasksSearch('')}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600"
                        >
                          <X className="w-2.5 h-2.5" />
                        </button>
                      )}
                    </div>

                    {/* View Selector Dropdown, left of the user filter like on the board */}
                    <div className="flex items-center space-x-1">
                      <span className="text-[11px] font-bold text-[#667085] dark:text-[#9aa2b1]">View:</span>
                      <div className="relative">
                        <select
                          value={myTasksView}
                          onChange={(e) => changeMyTasksView(e.target.value as MyTasksView)}
                          className="text-[11px] font-black pl-2 pr-6 py-1 rounded-xl bg-[#121417] text-white dark:bg-white dark:text-black border border-transparent shadow-sm focus:outline-none cursor-pointer appearance-none"
                          aria-label="Select Tasks View"
                        >
                          <option value="kanban">📌 Kanban Board</option>
                          <option value="list">📊 Grid / Spreadsheet</option>
                          <option value="timeline">⏱️ Gantt & Timeline</option>
                          <option value="calendar">📅 Calendar</option>
                        </select>
                        <ChevronDown className="w-3 h-3 text-brand-400 dark:text-brand-300 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none stroke-[2.5]" />
                      </div>
                    </div>

                    <TeamSelect selectedTeamIds={myTasksTeamIds} onChange={setMyTasksTeamIds} />

                    <ProjectMultiSelect
                      projects={myTasksProjects}
                      selectedIds={myTasksProjectIds}
                      onChange={setMyTasksProjectIds}
                    />

                    {can('task.create') && (
                      <button
                        type="button"
                        onClick={() => setIsCreateTaskOpen(true)}
                        className="px-2.5 py-1 rounded-xl bg-[#121417] dark:bg-white text-white dark:text-black text-[11px] font-bold flex items-center space-x-1 hover:opacity-90 transition-opacity"
                      >
                        <span>+ Task</span>
                      </button>
                    )}
                  </div>
                </div>
                {myTasksView === 'kanban' && (
                  <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
                  <KanbanBoard
                    tasks={visibleMyTasks}
                    hideOverdue={myTasksStage.hideOverdue}
                    hideCompleted={myTasksStage.hideCompleted}
                    onTaskClick={(t) => setActiveModalTaskId(t.id)}
                    onUpdateTaskStatus={handleUpdateMyTaskStatus}
                    onQuickCreateTask={quickCreateMyTask}
                    onRefreshTasks={fetchMyTasks}
                    hideUserFilter={true}
                  />
                  </div>
                )}
                {myTasksView === 'list' && (
                  <TaskTable
                    tasks={visibleMyTasks}
                    hideUserFilter
                    onTaskClick={(t) => setActiveModalTaskId(t.id)}
                    onUpdateTaskStatus={handleUpdateMyTaskStatus}
                    onQuickCreateTask={quickCreateMyTask}
                  />
                )}
                {myTasksView === 'timeline' && (
                  <TimelineView tasks={visibleMyTasks} onTaskClick={(t) => setActiveModalTaskId(t.id)} />
                )}
                {myTasksView === 'calendar' && (
                  <CalendarView tasks={visibleMyTasks} onTaskClick={(t) => setActiveModalTaskId(t.id)} />
                )}
              </div>
            )}

            {(currentView === 'workload' || currentView === 'team') && <TeamWorkloadView />}

            {currentView === 'chat' && user?.role !== 'guest' && (
              <ViewErrorBoundary viewName="Messages & Chat">
                <ChatWorkspace
                  key={chatTargetChannelId || 'chat'}
                  initialChannelId={chatTargetChannelId}
                  onPopOut={(channelId) => {
                    setCurrentView(lastNonChatView || 'boards');
                  }}
                />
              </ViewErrorBoundary>
            )}

            {currentView === 'meetings' && user?.role !== 'guest' && (
              <ViewErrorBoundary viewName="Meetings">
                <MeetingsWorkspace />
              </ViewErrorBoundary>
            )}

            {(currentView === 'teams' || currentView.startsWith('team-')) && user?.role !== 'guest' && (
              <ViewErrorBoundary viewName="Teams">
                <TeamsWorkspace
                  selectedTeamId={
                    currentView.startsWith('team-') ? currentView.replace('team-', '') : null
                  }
                  onSelectTeam={(teamId) => {
                    setCurrentView(teamId ? `team-${teamId}` : 'teams');
                  }}
                  onSelectProject={(projId) => {
                    setActiveBoardProjectId(projId);
                    setCurrentView(`project-${projId}`);
                  }}
                />
              </ViewErrorBoundary>
            )}

            {currentView === 'audit' && <AuditLogView />}

            {currentView === 'roles' &&
              (can('roles.manage') || can('security.view') ? (
                <ViewErrorBoundary viewName="Roles & Access">
                  <RolesPermissionsView />
                </ViewErrorBoundary>
              ) : (
                <div className="p-12 text-center text-xs text-rose-500 font-medium">
                  Access Denied. Your role does not have access to Roles & Access.
                </div>
              ))}
          </div>
        </main>
      </div>

      {/* Global Command Search Palette (Ctrl + K) */}
      <GlobalCommandPalette
        isOpen={isCommandPaletteOpen}
        onClose={() => setIsCommandPaletteOpen(false)}
        onSelectProject={(projId) => setCurrentView(`project-${projId}`)}
        onSelectTask={(taskId, projectId) => {
          if (projectId) {
            setCurrentView(`project-${projectId}`);
          }
          setActiveModalTaskId(taskId);
        }}
        onSelectChat={(channelId) => {
          setChatTargetChannelId(channelId);
          setCurrentView('chat');
        }}
      />

      {/* Quick Create Task Modal */}
      <CreateTaskModal
        isOpen={isCreateTaskOpen}
        onClose={() => setIsCreateTaskOpen(false)}
        projectId={
          currentView.startsWith('project-')
            ? currentView.replace('project-', '')
            : currentView === 'boards' && activeBoardProjectId
            ? activeBoardProjectId
            : undefined
        }
        onTaskCreated={() => {
          if (currentView === 'my-tasks' && token) {
            fetchMyTasks();
          }
        }}
      />

      {/* Task Detail Modal */}
      {activeModalTaskId && (
        <TaskDetailModal
          taskId={activeModalTaskId}
          isOpen={true}
          onClose={() => setActiveModalTaskId(null)}
          onTaskUpdated={() => {
            if (currentView === 'my-tasks' && token) {
              fetchMyTasks();
            }
          }}
        />
      )}

      {/* Global Interactive Video Call Modal (1-on-1 & Group calls) */}
      <VideoCallModal />

      {/* Global Incoming Call Prompt Modal */}
      <IncomingCallModal />

      {/* Interactive First-Time Guided Walkthrough Tour */}
      <OnboardingTourModal />

      {/* Universal Click-to-Chat User Profile Modal */}
      <UserProfileModal
        onNavigateToChat={(channelId) => {
          if (channelId) setChatTargetChannelId(channelId);
          setCurrentView('chat');
        }}
      />

      {/* Mobile App Bottom Navigation Bar (Smartphones) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-[#121417]/95 backdrop-blur-lg border-t border-surface-200 dark:border-surface-800 pb-safe px-2 py-1 shadow-lg flex items-center justify-around select-none">
        <button
          onClick={() => {
            setCurrentView('dashboard');
            setIsMobileMenuOpen(false);
          }}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
            currentView === 'dashboard'
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="Workspace Dashboard"
        >
          <LayoutDashboard className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">Workspace</span>
        </button>

        <button
          onClick={() => {
            setCurrentView('boards');
            setIsMobileMenuOpen(false);
          }}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
            currentView === 'boards' || currentView.startsWith('project-')
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="Boards"
        >
          <FolderKanban className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">Boards</span>
        </button>

        <button
          onClick={() => {
            setCurrentView('projects');
            setIsMobileMenuOpen(false);
          }}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
            currentView === 'projects'
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="Projects"
        >
          <Briefcase className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">Projects</span>
        </button>

        <button
          onClick={() => {
            setCurrentView('my-tasks');
            setIsMobileMenuOpen(false);
          }}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
            currentView === 'my-tasks'
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="My Tasks"
        >
          <CheckSquare className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">My Tasks</span>
        </button>

        <button
          onClick={() => {
            setCurrentView('chat');
            setIsMobileMenuOpen(false);
          }}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors relative ${
            currentView === 'chat'
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="Chat & Messages"
        >
          <div className="relative">
            <MessageSquare className="w-5 h-5" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1.5 w-2 h-2 rounded-full bg-brand-500 ring-2 ring-white dark:ring-[#121417]" />
            )}
          </div>
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">Messages</span>
        </button>

        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className={`flex flex-col items-center justify-center flex-1 py-1 transition-colors ${
            isMobileMenuOpen
              ? 'text-brand-600 dark:text-brand-400 font-bold'
              : 'text-surface-500 hover:text-surface-900 dark:hover:text-surface-100'
          }`}
          aria-label="More Navigation Options"
        >
          <Menu className="w-5 h-5" />
          <span className="text-[10px] mt-0.5 tracking-tight font-sans">More</span>
        </button>
      </nav>

      {/* Asks once whether to turn on desktop notifications */}
      <NotificationPermissionPrompt />

      {/* Bottom-Right Chatbot-Style Incoming Message Popup & Quick Reply Widget */}
      <ViewErrorBoundary viewName="Floating Chat">
        <FloatingChatPopup
          currentPage={currentView}
          onOpenFullChat={(channelId) => {
            if (channelId) setChatTargetChannelId(channelId);
            setCurrentView('chat');
            clearToast();
          }}
        />
      </ViewErrorBoundary>

      {/* Live Push Toast Alerts (for Task & System Notifications) */}
      {toast && toast.type !== 'chat' && toast.type !== 'chat_message' && (
        <div
          role="button"
          tabIndex={0}
          onClick={() => handleNotificationNavigate(toast)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleNotificationNavigate(toast);
            }
          }}
          title="Click to open"
          className="fixed bottom-20 md:bottom-6 right-4 sm:right-6 z-50 animate-slide-left w-[calc(100vw-2rem)] sm:w-auto max-w-sm bg-white dark:bg-surface-900 border border-brand-500/30 hover:border-brand-500 shadow-elevated rounded-2xl p-4 flex items-start space-x-3 cursor-pointer hover:scale-[1.01] active:scale-[0.99] transition-all group"
        >
          <div className="w-8 h-8 rounded-xl bg-brand-500/10 group-hover:bg-brand-600 text-brand-600 group-hover:text-white flex items-center justify-center shrink-0 transition-colors">
            <Check className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <h5 className="font-bold text-xs text-surface-900 dark:text-surface-100 group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">
              {toast.title}
            </h5>
            <p className="text-xs text-surface-600 dark:text-surface-400 mt-0.5 line-clamp-2">
              {toast.message}
            </p>
            <span className="text-[10px] font-semibold text-brand-600 dark:text-brand-400 mt-1 inline-block">
              Click to view →
            </span>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              clearToast();
            }}
            aria-label="Dismiss notification"
            className="p-1 text-surface-400 hover:text-surface-600 dark:hover:text-surface-200 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};
